import React, { useEffect, useState } from "react";
import apiService from "../services/apiService";
import I from "./Icon";
import BC from "./Breadcrumb";

const getBookId = (book) => Number(book?.id ?? book?.Id ?? book?.bookMasterId ?? book?.BookMasterId ?? 0);
const newRow = () => ({ id: 0, bookName: "", amount: "", qty: "", isActive: true });

const parseJson = (value) => {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return value; }
};

const unwrapArray = (value, depth = 0) => {
  if (depth > 8 || value == null) return [];
  const parsed = parseJson(value);
  if (Array.isArray(parsed)) return parsed;
  if (!parsed || typeof parsed !== "object") return [];
  for (const key of ["output", "Output", "result", "Result", "data", "Data", "items", "Items", "rows", "Rows"]) {
    if (parsed[key] != null) {
      const found = unwrapArray(parsed[key], depth + 1);
      if (found.length) return found;
    }
  }
  return [];
};

const getDetails = (book) => {
  const raw = book?.bokDetlst ?? book?.BokDetlst ?? book?.teaBooAssDetlst ?? book?.TeaBooAssDetlst ??
    book?.bookMasterDetails ?? book?.BookMasterDetails ?? book?.details ?? book?.Details;
  const parsed = parseJson(raw);
  if (Array.isArray(parsed) && parsed.length) return parsed;
  if (parsed && typeof parsed === "object") return [parsed];
  if (book?.bookName ?? book?.BookName) return [{
    id: 0, bookName: book?.bookName ?? book?.BookName,
    amount: book?.amount ?? book?.Amount ?? "", qty: book?.qty ?? book?.Qty ?? "",
  }];
  return [];
};

const makeRows = (book) => {
  const rows = getDetails(book).map((d) => ({
    id: Number(d?.id ?? d?.Id ?? 0),
    bookName: String(d?.bookName ?? d?.BookName ?? ""),
    amount: d?.amount ?? d?.Amount ?? "",
    qty: d?.qty ?? d?.Qty ?? "",
    isActive: d?.isActive ?? d?.IsActive ?? true,
  }));
  return rows.length ? rows : [newRow()];
};

const isActiveExam = (exam) => {
  const flag = exam?.isActive ?? exam?.IsActive;
  return flag === true || flag === 1 || flag === "1" || String(flag).toLowerCase() === "true";
};

const getApiError = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.errors) {
    const messages = Object.values(data.errors).flat().filter(Boolean).join(" ");
    if (messages) return messages;
  }
  return data?.failureReason ?? data?.FailureReason ?? data?.message ?? data?.Message ?? data?.title ?? error?.message ?? fallback;
};

const isSuccess = (response) => {
  const data = response?.data;
  if (data?.isSuccess === false || data?.IsSuccess === false) return false;
  const output = data?.output ?? data?.Output;
  const status = String(output?.status ?? output?.Status ?? "").toLowerCase();
  return !status.includes("duplicate") && !status.includes("fail") && !status.includes("error");
};

export default function BookMasterForm({ book = null, onSave, onBack }) {
  const bookId = getBookId(book);
  const isEdit = bookId > 0;
  const [examId, setExamId] = useState("");
  const [description, setDescription] = useState(String(book?.description ?? book?.Description ?? ""));
  const [rows, setRows] = useState(() => makeRows(book));
  const [competitiveExams, setCompetitiveExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(true);
  const [examsError, setExamsError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    setRows(makeRows(book));
    setDescription(String(book?.description ?? book?.Description ?? ""));
  }, [book]);

  useEffect(() => {
    let mounted = true;
    const loadExams = async () => {
      try {
        setExamsLoading(true);
        const response = await apiService.get("Exam/getAllExam");
        if (!isSuccess(response)) throw new Error(response?.data?.failureReason ?? "Could not load exams");
        const exams = unwrapArray(response?.data)
          .filter(isActiveExam)
          .map((e) => ({
            id: String(e?.id ?? e?.Id ?? e?.examId ?? e?.ExamId ?? ""),
            name: String(e?.examName ?? e?.ExamName ?? e?.name ?? e?.Name ?? ""),
          }))
          .filter((e) => e.id && e.name);
        if (mounted) { setCompetitiveExams(exams); setExamsError(""); }
      } catch (err) {
        if (mounted) { setCompetitiveExams([]); setExamsError(getApiError(err, "Unable to load active exams")); }
      } finally { if (mounted) setExamsLoading(false); }
    };
    loadExams();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const saved = String(book?.competitiveExams ?? book?.CompetitiveExams ?? book?.examId ?? book?.ExamId ?? "").trim();
    if (!saved) { setExamId(""); return; }
    const matched = competitiveExams.find((e) => e.id === saved || e.name.toLowerCase() === saved.toLowerCase());
    setExamId(matched?.id ?? saved);
  }, [book, competitiveExams]);

  const changeRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
    setFieldErrors({});
    setError("");
  };

  const addRow = () => { setRows((prev) => [...prev, newRow()]); setFieldErrors({}); };
  const removeRow = (index) => {
    setRows((prev) => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
    setFieldErrors({});
  };

  const totalQty = rows.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
  const totalAmount = rows.reduce((sum, row) => sum + (Number(row.amount) || 0) * (Number(row.qty) || 0), 0);

  const validate = () => {
    const errs = {};
    if (!competitiveExams.some((e) => e.id === String(examId))) errs.examId = "Please select an active competitive exam";
    rows.forEach((row, index) => {
      if (!String(row.bookName).trim()) errs[`name${index}`] = "Book name is required";
      if (row.amount === "" || !Number.isFinite(Number(row.amount)) || Number(row.amount) < 0)
        errs[`amount${index}`] = "Enter a valid amount";
      if (row.qty === "" || !Number.isInteger(Number(row.qty)) || Number(row.qty) < 0)
        errs[`qty${index}`] = "Enter a valid whole-number quantity";
    });
    return errs;
  };

  const handleSubmit = async () => {
    if (saving) return;
    const errors = validate();
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    const selectedExam = competitiveExams.find((e) => e.id === String(examId));
    const payload = {
      ...(isEdit ? { id: bookId } : {}),
      bookCode: isEdit ? String(book?.bookCode ?? book?.BookCode ?? "") : "",
      competitiveExams: selectedExam.name,
      totalAmount,
      qty: totalQty,
      description: description.trim(),
      isActive: true,
      bokDetlst: rows.map((row) => ({
        id: isEdit ? Number(row.id || 0) : 0,
        bookMasterId: isEdit ? bookId : 0,
        bookName: String(row.bookName).trim(),
        amount: Number(row.amount),
        qty: Number(row.qty),
        isActive: row.isActive === false ? false : true,
      })),
    };
    try {
      setSaving(true);
      setError("");
      console.log(isEdit ? "Update Book Payload:" : "Add Book Payload:", payload);
      const response = isEdit
        ? await apiService.put("BookMaster/updateBookMaster", payload)
        : await apiService.post("BookMaster/addBookMaster", payload);
      if (!isSuccess(response)) {
        const d = response?.data;
        throw new Error(d?.failureReason ?? d?.FailureReason ?? d?.output?.message ?? d?.message ?? "Book save failed");
      }
      if (onSave) await onSave(response?.data?.output ?? response?.data);
      if (onBack) onBack();
    } catch (err) {
      console.error(isEdit ? "Update Book API Error:" : "Add Book API Error:", err);
      setError(getApiError(err, "Unable to save books"));
    } finally { setSaving(false); }
  };

  const err = (key) => fieldErrors[key] ?
    <div style={{ color: "#dc2626", fontSize: 11, marginTop: 3 }}>{fieldErrors[key]}</div> : null;

  return (
    <>
      <BC items={["Book Master", isEdit ? "Update Book" : "Add Book"]} onBack={onBack} />
      <div className="card cp" style={{ maxWidth: 850 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18 }}>
          {isEdit ? "Update Book" : "Add Book"}
        </div>
        {error && <div style={{ color: "#b42318", background: "#fef3f2", padding: 12, borderRadius: 8, marginBottom: 16 }}>{error}</div>}
        <div className="fsec">
          <div className="fsec-t"><I n="book" s={14} c="var(--p)" /> Book Information</div>
          <div className="fgrid">
            <div className="fg">
              <label className="fl">Book Code</label>
              <input className="fi" value={isEdit ? (book?.bookCode ?? book?.BookCode ?? "") : "Auto Generated"} disabled readOnly />
            </div>
            <div className="fg">
              <label className="fl">Competitive Exams *</label>
              <select className="fi" value={examId} disabled={examsLoading || saving} onChange={(e) => { setExamId(e.target.value); setFieldErrors({}); }}>
                <option value="">{examsLoading ? "Loading active exams..." : "Select Competitive Exam"}</option>
                {competitiveExams.map((exam) => <option key={exam.id} value={exam.id}>{exam.name}</option>)}
                {examId && !competitiveExams.some((exam) => exam.id === String(examId)) &&
                  <option value={examId} disabled>Previously selected exam (inactive/unavailable)</option>}
              </select>
              {examsError && <div style={{ color: "#dc2626", fontSize: 11 }}>{examsError}</div>}
              {err("examId")}
            </div>
            <div className="fg fall">
              <label className="fl">Description</label>
              <textarea className="fi" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Enter description..." />
            </div>
          </div>
        </div>
        <div className="fsec" style={{ marginTop: 18 }}>
          <div className="fsec-t" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span><I n="book" s={14} c="var(--p)" /> Books / Question Sets ({rows.length})</span>
            <button type="button" className="btn btn-g btn-sm" disabled={saving} onClick={addRow}>+ Add Another Book</button>
          </div>
          {rows.map((row, index) => (
            <div key={index} style={{ padding: "14px 12px", marginBottom: 12, border: "1px solid #e2e8f0", borderRadius: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <strong style={{ fontSize: 13 }}>Book {index + 1}</strong>
                {rows.length > 1 && <button type="button" className="btn btn-d btn-sm" disabled={saving} onClick={() => removeRow(index)}>Remove</button>}
              </div>
              <div className="fgrid">
                <div className="fg">
                  <label className="fl">Book/QuestionSet Name *</label>
                  <input className="fi" value={row.bookName} disabled={saving} onChange={(e) => changeRow(index, "bookName", e.target.value)} placeholder="e.g. Mathematics" />
                  {err(`name${index}`)}
                </div>
                <div className="fg">
                  <label className="fl">Amount *</label>
                  <input className="fi" type="number" min="0" step="0.01" value={row.amount} disabled={saving} onChange={(e) => changeRow(index, "amount", e.target.value)} placeholder="Enter amount" />
                  {err(`amount${index}`)}
                </div>
                <div className="fg">
                  <label className="fl">Quantity *</label>
                  <input className="fi" type="number" min="0" step="1" value={row.qty} disabled={saving} onChange={(e) => changeRow(index, "qty", e.target.value)} placeholder="Enter quantity" />
                  {err(`qty${index}`)}
                </div>
                <div className="fg">
                  <label className="fl">Line Total</label>
                  <div className="fi" style={{ background: "#f8fafc" }}>₹{((Number(row.amount) || 0) * (Number(row.qty) || 0)).toLocaleString("en-IN")}</div>
                </div>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 20, margin: "12px 0 20px", fontWeight: 600, fontSize: 13 }}>
            <span>Total Quantity: {totalQty}</span>
            <span>Total Amount: ₹{totalAmount.toLocaleString("en-IN")}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 9 }}>
          <button type="button" className="btn btn-p" disabled={saving} onClick={handleSubmit}>
            <I n="check" s={13} c="#fff" /> {saving ? "Saving..." : isEdit ? "Update Book" : "Add Book"}
          </button>
          <button type="button" className="btn btn-g" disabled={saving} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </>
  );
}
